import * as storage from '../../utils/storage';
import ApolloLinkTimeout from 'apollo-link-timeout';
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, createHttpLink } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';
import { URL, getChannel} from '../config';
import { captureException } from '../../utils/sentry/sentry';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';

const LOG_APOLLO_REQUEST = process.env.EXPO_PUBLIC_LOG_APOLLO_REQUEST === 'true';
const LOG_APOLLO_RESPONSE = process.env.EXPO_PUBLIC_LOG_APOLLO_RESPONSE === 'true';

const authLink = setContext(async (_, { headers }) => {
  // get the authentication token from local storage if it exists
  // return the headers to the context so httpLink can read them
  const data = await storage.loadString('token');


  return {
    headers: {
      ...headers,
      authorization: data ? `Bearer ${data}` : '',
    },
  };
});

const timeoutLink =  new ApolloLinkTimeout(60000);

const operationsWithErrorsBeingOk = ['login', 'register', 'registerFromAnonymous', 'retrievePublicReckoning', 'currentReckoning', 'silentLogin', 'wpoRequest', 'closeActiveWpoVideoRequest'];

const channel = getChannel();

const errorLink = onError(({ graphQLErrors : graphQLErrors, networkError, response, operation }) => {
  if(response?.errors) {
    if(!graphQLErrors) {
      graphQLErrors = response.errors;
    }
  }
  if (graphQLErrors) {
    graphQLErrors.map(graphQLError => {
      const err = graphQLError as any;
      const messageString = `ERR[GraphQL(${operation.operationName})]: Message: ${err?.message}, Location: ${err?.locations},
       Path: ${err?.path}, Error Code: ${err?.code}`;
      if (-1 === operationsWithErrorsBeingOk.findIndex(el => el === operation?.operationName) || err?.code !== 403) {
        if (channel === 'production') {
          console.error(graphQLErrors, messageString);
          captureException(messageString);

        } else {
          console.error(graphQLErrors, messageString);
        }
      }

    }

    );
  } else if (networkError) {
    const messageString = `[Network error]: ${networkError}`;
    if (channel === 'production') {
      captureException(messageString);

    } else {
      console.error(networkError);
    }
  }
});

const retryLink = new RetryLink({
  delay: {
    initial: 100,
    max: Infinity,
    jitter: true
  },
  attempts: {
    max: 2,
    retryIf: (error, _operation) => {
      const def = getMainDefinition(_operation.query);
      const operation = 'operation' in def ? def.operation : undefined;
      const doNotRetryCodes = [500];
      return !!error && !doNotRetryCodes.includes(error.statusCode) && operation === 'query' && def.name?.value !== 'customSession';
    }
  }
});

const loggingFetch: HttpLink['options']['fetch'] = (input, init) => {
  const body = JSON.parse(init?.body as string || '{}');
  console.log(`📡 ${body.operationName || ''}\n ${body.query}`, body.variables);
  return fetch(input, init);
};
const logResponse = new ApolloLink((operation, forward) => {
  return forward(operation).map(response => {
    console.log('📥', JSON.stringify(response.data, null, 2));
    return response;
  });
});

export const clientFunction = (language: string) => {
  const URL_WITH_LANGUAGE = URL + language + '/graphql';
  const httpLinkLanguage = createHttpLink({
    uri: URL_WITH_LANGUAGE,
    fetch: LOG_APOLLO_REQUEST ? loggingFetch : undefined,
  });

  const apolloLinkConfig = [
    ...(LOG_APOLLO_RESPONSE ?  [logResponse] : []),
    authLink,
    retryLink,
    errorLink,
    timeoutLink,
    httpLinkLanguage
  ];
  return new ApolloClient({
    link: ApolloLink.from(apolloLinkConfig),
    cache: new InMemoryCache({
      typePolicies: {
        User: {
          fields: {
            matching: {
              merge: false
            }
          }
        },
        UserMatching: {
          fields: {
            value: {
              merge: false
            },
          },
        },
      }
    }),
  });
};


export const client = clientFunction('en');

